import { useEffect, useState } from "react";
import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  Activity,
  BookOpenText,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CircleGauge,
  ClipboardCheck,
  LogOut,
  Menu,
  MessageSquareText,
  Route,
  Workflow,
  Search,
  Settings,
  Sparkles,
  X,
} from "lucide-react";
import { Topbar, VoxMark } from "./components";
import {
  AnalyticalContextBar,
  AnalyticalContextProvider,
} from "./analytical-context";

const primaryNav = [
  { to: "/overview", label: "Overview", icon: CircleGauge },
  { to: "/signals", label: "Signals", icon: Activity },
  { to: "/voice", label: "Customer Voice", icon: MessageSquareText },
  { to: "/demand", label: "Demand", icon: ChartNoAxesCombined },
  { to: "/failure-demand", label: "Failure Demand", icon: CircleAlert },
  { to: "/root-causes", label: "Root Causes", icon: Workflow },
  { to: "/journeys", label: "Journeys", icon: Route },
  { to: "/evidence", label: "Evidence", icon: BookOpenText },
  { to: "/actions", label: "Actions", icon: ClipboardCheck },
] as const;

export function AppShell({ onLogout }: { onLogout: () => void }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const showAnalyticalContext = ![
    "/settings",
    "/taxonomy",
    "/review-queue",
    "/metric-definitions",
  ].includes(pathname);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  function logout() {
    window.localStorage.removeItem("vox-demo-session");
    onLogout();
  }

  return (
    <div className="app-frame">
      <button
        className="mobile-menu"
        type="button"
        onClick={() => setMobileOpen(true)}
      >
        <Menu size={20} />
        <span>Menu</span>
      </button>
      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__top">
          <VoxMark />
          <button
            className="sidebar-close"
            type="button"
            onClick={() => setMobileOpen(false)}
          >
            <X size={19} />
          </button>
        </div>
        <div className="organisation-switcher">
          <span className="organisation-mark">N</span>
          <div>
            <strong>Northstar Group</strong>
            <small>United Kingdom</small>
          </div>
          <span className="chevrons" aria-hidden="true">
            <ChevronUp size={12} />
            <ChevronDown size={12} />
          </span>
        </div>
        <nav className="nav-list" aria-label="Primary navigation">
          <span className="nav-label">Intelligence</span>
          {primaryNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              activeProps={{ className: "nav-item--active" }}
              className="nav-item"
              onClick={() => setMobileOpen(false)}
            >
              <item.icon size={18} strokeWidth={1.7} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className="sidebar__bottom">
          <Link
            to="/ask"
            activeProps={{ className: "ask-card--active" }}
            className="ask-card"
          >
            <span>
              <Sparkles size={17} /> Ask Vox
            </span>
            <small>Investigate your customer data</small>
          </Link>
          <Link
            to="/settings"
            className="sidebar-utility"
            activeProps={{ className: "nav-item--active" }}
            onClick={() => setMobileOpen(false)}
          >
            <Settings size={17} /> Settings
          </Link>
          <button className="sidebar-utility" type="button" onClick={logout}>
            <LogOut size={17} /> Sign out
          </button>
          <div className="data-freshness">
            <span />
            <div>
              <strong>Data is current</strong>
              <small>Updated 4 minutes ago</small>
            </div>
          </div>
        </div>
      </aside>
      {mobileOpen && (
        <button
          className="sidebar-backdrop"
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
        />
      )}
      <AnalyticalContextProvider>
        <div className="app-main">
          <Topbar onOpenSearch={() => setSearchOpen(true)} />
          {showAnalyticalContext && <AnalyticalContextBar />}
          <main className="page-content">
            <Outlet />
          </main>
        </div>
      </AnalyticalContextProvider>
      {searchOpen && (
        <div
          className="command-backdrop"
          role="presentation"
          onMouseDown={() => setSearchOpen(false)}
        >
          <div
            className="command-palette"
            role="dialog"
            aria-modal="true"
            aria-label="Search intelligence"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="command-input">
              <Search size={18} />
              <input
                autoFocus
                placeholder="Search themes, signals and interactions…"
              />
              <kbd>Esc</kbd>
            </div>
            <div className="command-results">
              <span className="command-label">Suggested</span>
              <button
                type="button"
                onClick={() => {
                  void navigate({ to: "/signals" });
                  setSearchOpen(false);
                }}
              >
                <Activity size={17} />
                <div>
                  <strong>Identity verification failures</strong>
                  <small>Emerging signal · 1,842 interactions</small>
                </div>
                <span>Open</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  void navigate({ to: "/demand" });
                  setSearchOpen(false);
                }}
              >
                <ChartNoAxesCombined size={17} />
                <div>
                  <strong>Payment date confusion</strong>
                  <small>Contact driver · +24% this period</small>
                </div>
                <span>Open</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  void navigate({ to: "/ask" });
                  setSearchOpen(false);
                }}
              >
                <Sparkles size={17} />
                <div>
                  <strong>Ask a question</strong>
                  <small>Investigate across all customer intelligence</small>
                </div>
                <span>Open</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
